'use client';

/**
 * Contact Page
 * Comprehensive inquiry form with photo upload capability
 */

import { useState } from 'react';
import { Navigation } from '@/components/navigation';
import { Footer } from '@/components/footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Phone, Mail, MapPin, Upload, CheckCircle2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

export default function ContactPage() {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    message: '',
  });
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setSelectedFiles(Array.from(e.target.files));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Get backend URL
      const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL || 'http://localhost:6001';

      // Submit form to backend
      const response = await fetch(`${backendUrl}/api/v1/contact/inquiry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to submit inquiry');
      }

      setIsSubmitting(false);
      toast({
        title: 'Inquiry Received!',
        description: `Thank you for contacting us. We'll get back to you within 24 hours at ${formData.email}`,
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        address: '',
        message: '',
      });
      setSelectedFiles([]);
    } catch (error) {
      setIsSubmitting(false);
      console.error('Form submission error:', error);
      toast({
        title: 'Error',
        description: 'Failed to submit your inquiry. Please try again or contact us directly at jojojocelyn850@gmail.com',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-foreground to-foreground/90 text-background">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 text-yellow-400">Get In Touch</h1>
          <p className="text-xl md:text-2xl text-background/90 max-w-3xl mx-auto leading-relaxed">
            Ready to start your project? We'd love to hear from you.
            Reach out for a free consultation and quote.
          </p>
        </div>
      </section>

      {/* Contact Form and Info */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-12 max-w-7xl mx-auto">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle className="text-3xl">Send Us Your Inquiry</CardTitle>
                  <CardDescription className="text-base">
                    Fill out the form below and we'll get back to you within 24 hours.
                    For faster service, give us a call!
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Name */}
                    <div>
                      <Label htmlFor="name" className="text-base">
                        Full Name <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="John Doe"
                        required
                        className="mt-2"
                      />
                    </div>

                    {/* Email and Phone */}
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="email" className="text-base">
                          Email Address <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="john@example.com"
                          required
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone" className="text-base">
                          Phone Number <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="(123) 456-7890"
                          required
                          className="mt-2"
                        />
                      </div>
                    </div>

                    {/* Address */}
                    <div>
                      <Label htmlFor="address" className="text-base">
                        Address <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="address"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        placeholder="123 Main Street, City, State, ZIP"
                        required
                        className="mt-2"
                      />
                    </div>

                    {/* Message */}
                    <div>
                      <Label htmlFor="message" className="text-base">
                        Project Details <span className="text-destructive">*</span>
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        placeholder="Tell us about your project... What furniture needs restoration? What's your vision for the space?"
                        required
                        rows={6}
                        className="mt-2"
                      />
                    </div>

                    {/* Photo Upload */}
                    <div>
                      <Label htmlFor="photos" className="text-base">
                        Upload Photos (Optional)
                      </Label>
                      <div className="mt-2 border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary/50 transition-colors">
                        <Upload className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                        <p className="text-sm text-muted-foreground mb-2">
                          Click to upload or drag and drop
                        </p>
                        <p className="text-xs text-muted-foreground mb-4">
                          PNG, JPG up to 10MB each
                        </p>
                        <Input
                          id="photos"
                          name="photos"
                          type="file"
                          multiple
                          accept="image/*"
                          onChange={handleFileChange}
                          className="hidden"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => document.getElementById('photos')?.click()}
                        >
                          Select Photos
                        </Button>
                      </div>
                      {selectedFiles.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {selectedFiles.map((file, index) => (
                            <Badge key={index} variant="secondary" className="text-sm">
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              {file.name}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      size="lg"
                      className="w-full bg-primary hover:bg-primary/90 text-lg py-6"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Sending...' : 'Send Inquiry'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Contact Information Sidebar */}
            <div className="space-y-6">
              {/* Phone */}
              <Card className="border-2 border-primary/20">
                <CardHeader>
                  <div className="p-3 bg-primary/10 rounded-lg w-fit">
                    <Phone className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle className="text-xl">Call Us</CardTitle>
                  <CardDescription>For immediate assistance</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <a
                      href="tel:09176260243"
                      className="text-2xl font-bold text-primary hover:underline block"
                    >
                      0917 626 0243
                    </a>
                    <a
                      href="tel:09194319735"
                      className="text-lg font-semibold text-primary/80 hover:underline block"
                    >
                      0919 431 9735
                    </a>
                  </div>
                </CardContent>
              </Card>

              {/* Email */}
              <Card className="border-2 border-primary/20">
                <CardHeader>
                  <div className="p-3 bg-primary/10 rounded-lg w-fit">
                    <Mail className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle className="text-xl">Email Us</CardTitle>
                  <CardDescription>We respond within 24 hours</CardDescription>
                </CardHeader>
                <CardContent>
                  <a
                    href="mailto:jojojocelyn850@gmail.com"
                    className="text-lg font-semibold text-primary hover:underline break-all"
                  >
                    jojojocelyn850@gmail.com
                  </a>
                </CardContent>
              </Card>

              {/* Location */}
              <Card className="border-2 border-primary/20">
                <CardHeader>
                  <div className="p-3 bg-primary/10 rounded-lg w-fit">
                    <MapPin className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle className="text-xl">Location</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-foreground/80 leading-relaxed font-semibold">
                    Tanza, Cavite<br />
                    Philippines
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
